import { attributeFactors, attributeKnobsOf, effectiveAttribute } from "./attribute-rules.ts";
import { levelRow } from "./levels.ts";
import type { Rules } from "./types.ts";

/**
 * The stats a character's attributes feed (ADR-138 decision 8 and its amendment of 2026-10-31, decision 5), as a page
 * prints them: from the same `attributeFactors` the map server caches, so the character window's preview cannot
 * disagree with the server's result.
 */

const IDENTITY = 10_000;

/**
 * The derived stats in the order the preview lists them. `maxHealth` and `maxFocus` are points; `critical` and `dodge`
 * are chances in basis points; every other is a change in basis points from none (positive is more).
 */
export const DERIVED_STAT_IDS = Object.freeze(["maxHealth", "maxFocus", "physicalDealt", "physicalTaken", "nonPhysicalDealt", "critical", "dodge", "moveSpeed", "blowCooldown", "focusRegeneration", "controlDuration"] as const);

/**
 * One stat of `DERIVED_STAT_IDS`.
 */
export type DerivedStatId = (typeof DERIVED_STAT_IDS)[number];

/**
 * Every derived stat by id.
 */
export type DerivedStats = Readonly<Record<DerivedStatId, number>>;

/**
 * One stat a pending choice moves.
 */
export interface StatChange {
  readonly id: DerivedStatId;
  readonly before: number;
  readonly after: number;
}

/**
 * The derived stats of a character.
 *
 * @param level The character's level, for its pools.
 * @param effective The seven effective attribute values in `ATTRIBUTES` order.
 * @param rules The rules: the attribute knobs, the levels and the PvE critical base.
 * @returns The stats by id.
 * @example
 * derivedStats(1, [5, 5, 5, 5, 5, 5, 5], rules).maxHealth === levelRow(rules.tables.levels, 1).pools.health;
 */
export function derivedStats(level: number, effective: readonly number[], rules: Pick<Rules, "constants" | "tables" | "crits">): DerivedStats {
  const factors = attributeFactors(effective, attributeKnobsOf(rules));
  const pools = levelRow(rules.tables.levels, level).pools;
  return {
    maxHealth: Math.floor((pools.health * factors.maxHealth) / IDENTITY),
    maxFocus: Math.floor((pools.focus * factors.maxFocus) / IDENTITY),
    physicalDealt: factors.physicalDealt - IDENTITY,
    physicalTaken: factors.physicalTaken,
    nonPhysicalDealt: factors.nonPhysicalDealt - IDENTITY,
    critical: rules.crits.pve.chanceBasisPoints + factors.critical,
    dodge: factors.dodge,
    moveSpeed: factors.moveSpeed,
    blowCooldown: factors.blowCooldown,
    focusRegeneration: factors.focusRegeneration - IDENTITY,
    controlDuration: factors.controlDuration,
  };
}

/**
 * The derived stats of stored values under effects' flat points, through `effectiveAttribute`'s clamp, the one the map
 * server's effective values take (`Attributes.recompute`): the character window previews a pending choice with this,
 * so a value an effect holds at a clamp previews as the server will hold it.
 *
 * @param level The character's level.
 * @param stored The seven stored values, with any pending points added, in `ATTRIBUTES` order.
 * @param flat The effects' flat points per attribute, in the same order.
 * @param rules The rules.
 * @returns The stats by id.
 */
export function derivedStatsOf(level: number, stored: readonly number[], flat: readonly number[], rules: Pick<Rules, "constants" | "tables" | "crits">): DerivedStats {
  const knobs = attributeKnobsOf(rules);
  return derivedStats(level, stored.map((value, index) => effectiveAttribute(value, flat[index] ?? 0, knobs)), rules);
}

/**
 * The stats that differ between two sets, in `DERIVED_STAT_IDS` order.
 *
 * @param before The stats now.
 * @param after The stats with the pending choice.
 * @returns Each changed stat with both values; empty when nothing changes.
 */
export function changedStats(before: DerivedStats, after: DerivedStats): StatChange[] {
  return DERIVED_STAT_IDS.filter((id) => before[id] !== after[id]).map((id) => ({ id, before: before[id], after: after[id] }));
}
